export type AggregationMode = "none" | "daily" | "weekly" | "monthly";

export type PresetName =
  | "default"
  | "availability"
  | "performance-ratio"
  | "gen-vs-forecast"
  | "demand-vs-forecast";

// Linkable raw-readings series presets. "custom" is the state after the user
// hand-edits the selection (not a URL-serialisable named preset).
export type RawPresetName =
  | "production-export"
  | "inverter-temperature"
  | "custom";

export type Formatters = {
  toLocaleString: (utcTimestamp: string | number) => string;
  toDateTimeString: (utcTimestamp: string | number) => string;
  toDateString: (utcTimestamp: string | number) => string;
  shiftToSiteTimezone: (utcTimestamp: string | number) => number;
  dateRangeToTimestamps: (
    startDate: string,
    endDate: string
  ) => { startMs: number; endMs: number };
  getTime: (utcTimestamp: string | number) => number;
  timeZone: string;
};

export const colors = {
  demand: "#1f3c88",
  behindMeterPV: "#fdee1eff",
  behindMeterBattery: "#ffa569ff",
  generation: "#d35400",
  import: "#c2e2ffff",
  export: "#6776ffff",
  batteryChargeFromPV: "#4ecdc4ff",
  batteryChargeFromGrid: "#b469ffff",
  batteryDischargeGrid: "#2fffafff",
  batteryAuxDemand: "#6e4f4cff",
  pvAuxDemand: "#a8763e",
  batteryFinalCharge: "#777777",
  exportRaw: "#ff6b6bff",
  exportMetered: "#ff6600",
  networkImport: "#2e86c1",
  forecastGeneration: "#e67e22",
  forecastGenerationP90: "#a85600",
  forecastDemand: "#5b7fbf",
  weatherActualsInrange: "#2ecc71",
  performanceRatio: "#9b59b6",
  expectedGenPeriods: "#f5c842",
  unavailableGeneration: "#e74c3c",
  healthEvents: "#fd7e14",
};

// How a raw series is drawn. "value" plots the reading itself; "delta" plots the
// change since the previous reading.
//
// Counter-based feeds need this. Solis and Reduxi report cumulative registers
// (INVERTER_E_TODAY, the Reduxi energy counters), which draw as a monotonic ramp
// that says nothing about WHEN energy was produced - the information is entirely
// in the gradient. SolarEdge reports per-period values, where "value" is already
// what you want, so this is off by default.
export type RenderMode = "value" | "delta";

// Whether to drop isolated extreme points from a rendered series.
//
// A counter that resets daily (Solis INVERTER_E_TODAY) produces one enormous
// negative delta at the rollover, which sets the y-axis scale and flattens the
// real data into a line near zero. Hiding it is the difference between a chart
// that shows the day and one that shows the reset.
//
// Default "show", always: this discards real readings, so it must be something
// the operator turns ON, never something a chart does to them quietly.
export type OutlierMode = "show" | "hide";
